import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Trash2, Calendar, Image as ImageIcon } from 'lucide-react';
import { useState } from 'react';

import type { CreateFlashDealRequest } from '@terra/contracts';
import { Badge, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

import { FlashDealProductsManager } from './components/FlashDealProductsManager';

export function FlashDealsPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingDeal, setEditingDeal] = useState<any>(null);
  const [managingDeal, setManagingDeal] = useState<any>(null);

  const { data: dealsResponse, isLoading } = useQuery({
    queryKey: ['flash-deals'],
    queryFn: () => api.marketing.listFlashDeals(),
  });

  const deals = dealsResponse?.data?.items || [];

  const createMutation = useMutation({
    mutationFn: (data: CreateFlashDealRequest) => api.marketing.createFlashDeal(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
      setShowAdd(false);
      setEditingDeal(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateFlashDealRequest> }) =>
      api.marketing.updateFlashDeal(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
      setShowAdd(false);
      setEditingDeal(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deleteFlashDeal(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: any = {
      title: formData.get('title') as string,
      slug: (formData.get('title') as string).toLowerCase().replace(/\s+/g, '-'),
      startsAt: new Date(formData.get('startsAt') as string).toISOString(),
      endsAt: new Date(formData.get('endsAt') as string).toISOString(),
      active: true,
      isFeatured: true,
      bannerImageUrl: (formData.get('bannerImageUrl') as string) || '',
    };
    if (editingDeal) {
      updateMutation.mutate({ id: editingDeal.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getStatus = (deal: any) => {
    const now = new Date();
    const start = new Date(deal.startsAt);
    const end = new Date(deal.endsAt);
    if (!deal.active) return { label: 'Inactive', tone: 'neutral' as const };
    if (now < start) return { label: 'Scheduled', tone: 'warning' as const };
    if (now > end) return { label: 'Expired', tone: 'neutral' as const };
    return { label: 'Live', tone: 'success' as const };
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Add form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Zap className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-ink text-[15px] font-extrabold">
                  {editingDeal ? 'Edit Flash Deal' : 'New Flash Deal'}
                </h3>
                <p className="text-ink-muted text-[12px]">
                  Terra Shoppy pays the whole discount; sellers get their full price. Offers sellers
                  fund themselves are reviewed under Seller offers.
                </p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <label className="text-ink mb-1 block text-[13px] font-bold">Deal Title</label>
                <Input
                  name="title"
                  required
                  placeholder="e.g. Summer Blowout Sale"
                  defaultValue={editingDeal?.title}
                />
              </div>
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">
                  Banner Image URL
                </label>
                <Input
                  name="bannerImageUrl"
                  placeholder="https://..."
                  defaultValue={editingDeal?.bannerImageUrl}
                />
              </div>
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Starts At</label>
                <Input
                  name="startsAt"
                  type="datetime-local"
                  required
                  defaultValue={editingDeal?.startsAt?.slice(0, 16)}
                />
              </div>
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Ends At</label>
                <Input
                  name="endsAt"
                  type="datetime-local"
                  required
                  defaultValue={editingDeal?.endsAt?.slice(0, 16)}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingDeal
                      ? 'Update Deal'
                      : 'Save Deal'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deals Table */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Zap className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Flash Deals</span>
          </div>
          <button
            onClick={() => {
              setEditingDeal(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Create Flash Deal'}
          </button>
        </div>

        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : deals.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Zap className="text-ink-muted h-10 w-10" />}
                title="No Flash Deals"
                description="Create a flash deal to boost sales with time-limited offers."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Deal Title</th>
                    <th className="py-3 pr-4">Duration</th>
                    <th className="py-3 pr-4 text-center">Products</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {deals.map((deal: any, idx: number) => {
                    const status = getStatus(deal);
                    return (
                      <tr key={deal.id} className="hover:bg-surface-subtle/30 transition-colors">
                        <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                          {(idx + 1).toString().padStart(2, '0')}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-3">
                            {deal.bannerImageUrl && (
                              <div className="border-border/40 bg-surface-subtle h-9 w-14 shrink-0 overflow-hidden rounded-lg border">
                                <img
                                  src={deal.bannerImageUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            )}
                            <span className="text-ink font-semibold">{deal.title}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="space-y-0.5">
                            <div className="text-ink-muted flex items-center gap-1.5 text-[12px] font-medium">
                              <Calendar className="h-3 w-3 shrink-0" />
                              <span>{new Date(deal.startsAt).toLocaleString()}</span>
                            </div>
                            <div className="text-ink-muted flex items-center gap-1.5 text-[12px] font-medium">
                              <Calendar className="h-3 w-3 shrink-0" />
                              <span>{new Date(deal.endsAt).toLocaleString()}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-center">
                          <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex min-w-[32px] items-center justify-center rounded-lg border px-2 py-0.5 text-[12px] font-extrabold">
                            {deal._count?.products || 0}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-center">
                          <Badge tone={status.tone}>{status.label}</Badge>
                        </td>
                        <td className="py-3 pr-5">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setManagingDeal(deal)}
                              className="bg-surface-subtle text-ink-muted hover:text-ink border-border/40 inline-flex items-center rounded-lg border px-2 py-1 text-xs font-bold transition-colors"
                            >
                              Products
                            </button>
                            <button
                              onClick={() => {
                                setEditingDeal(deal);
                                setShowAdd(true);
                              }}
                              className="text-brand-primary/80 hover:text-brand-primary flex h-8 items-center justify-center rounded-lg px-2 text-xs font-bold transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete "${deal.title}"?`))
                                  deleteMutation.mutate(deal.id);
                              }}
                              className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {managingDeal && (
        <FlashDealProductsManager deal={managingDeal} onClose={() => setManagingDeal(null)} />
      )}
    </div>
  );
}
